import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { Bell } from "lucide-react";
import { useTherapist } from "../../../context/TherapistProvider";
import { NotificationsAPI } from "../../../modules/notification/infrastructure/api/notifications.api";
import type { Notification } from "../../../modules/notification/domain/Notification";
import { useTranslation } from "react-i18next";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

export default function NotificationsMenu() {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const { isMobile } = useBreakpoint();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const fetchNotifications = useCallback(async () => {
        if (!therapist?.id) return;
        try {
            const res = await NotificationsAPI.get_unread();
            setNotifications(res.data);
        } catch (err) {
            console.error("Failed to fetch notifications", err);
        }
    }, [therapist?.id]);

    useEffect(() => {
        fetchNotifications();

        // Poll every 30 seconds for background job completions
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [therapist?.id, fetchNotifications]);


    const handleRead = async (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        try {
            await NotificationsAPI.mark_as_read(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (err) {
            console.error("Failed to mark notification as read", err);
        }
    };

    return (
        <div className={isMobile ? "" : "relative"}>
            <button
                data-tour="notifications"
                onClick={() => setIsOpen(!isOpen)}
                className="relative flex items-center justify-center p-2 text-content-tertiary hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-all duration-200"
            >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex w-2 h-2">
                        <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full opacity-75 animate-ping"></span>
                        <span className="relative inline-flex w-2 h-2 bg-red-500 rounded-full"></span>
                    </span>
                )}
            </button>

            {isOpen && (
                <div className={`
                    ${isMobile 
                        ? "fixed top-20 right-4 left-4 sm:left-auto sm:w-80 shadow-raised-lg" 
                        : "absolute right-0 mt-3 w-80 shadow-raised"
                    } 
                    bg-surface-raised border border-border-subtle rounded-2xl z-50 overflow-hidden
                `}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                        <h5 className="text-sm font-bold text-content-primary">
                            {t("notifications.title", "Notifications")}
                        </h5>
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-600 bg-brand-500/10 px-2.5 py-1 rounded-full dark:text-brand-400">
                            {notifications.length} {t("notifications.new", "new")}
                        </span>
                    </div>

                    <div className="max-h-96 overflow-y-auto w-full flex flex-col custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="p-10 text-center flex flex-col items-center gap-3">
                                <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-full text-content-tertiary">
                                    <Bell className="w-6 h-6 opacity-20" />
                                </div>
                                <p className="text-sm text-gray-500 font-medium italic">
                                    {t("notifications.empty", "No new notifications")}
                                </p>
                            </div>
                        ) : (
                            notifications.map((notif) => (
                                <Link
                                    key={notif.id}
                                    to={notif.link ?? (notif.session_id ? `/session/${notif.session_id}` : "#")}
                                    onClick={() => setIsOpen(false)}
                                    className="flex flex-col gap-1 p-5 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 group"
                                >
                                    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium leading-relaxed group-hover:text-gray-900 dark:group-hover:text-white">
                                        {t(notif.message, { title: notif.session_title })}
                                    </p>
                                    <div className="flex items-center justify-between mt-2">
                                        <span className="text-xs text-content-tertiary font-medium uppercase tracking-tight">
                                            {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <button
                                            onClick={(e) => handleRead(notif.id, e)}
                                            className="text-xs font-bold text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 uppercase tracking-widest"
                                        >
                                            {t("notifications.markRead", "Mark read")}
                                        </button>
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Background overlay for mobile/clicking outside */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
}
